import ms from "ms";
import { useEffect, useMemo, useState } from "react";

const UNIT_DURATION = {
  second: ms("1 second"),
  minute: ms("1 minute"),
  hour: ms("1 hour"),
  day: ms("1 day"),
  week: ms("1 week"),
  month: ms("30 days"),
  year: ms("1 year"),
};

const JUST_NOW_THRESHOLD = ms("5 seconds");

function useRelativeTime(
  date: Date,
  options: Pick<Intl.RelativeTimeFormatOptions, "numeric" | "style">,
) {
  const [now, setNow] = useState(Date.now());

  const value = date.getTime();
  const delta = Math.abs(now - value);
  const unit = getUnit(delta) || "second";

  const isJustNow =
    options.numeric === "auto" &&
    unit === "second" &&
    delta < JUST_NOW_THRESHOLD;

  const formatter = useMemo(
    () =>
      new Intl.RelativeTimeFormat(navigator.languages, {
        numeric: options.numeric,
        style: options.style,
      }),
    [options.numeric, options.style],
  );

  useEffect(() => {
    let tickFrequency: number;

    if (isJustNow) {
      tickFrequency = JUST_NOW_THRESHOLD - delta;
    } else {
      const unitDuration = UNIT_DURATION[unit];
      const deltaUnits = (value - now) / unitDuration;
      const roundedDeltaUnits =
        deltaUnits > 0 ? Math.ceil(deltaUnits) : Math.floor(deltaUnits);
      const timeUntilNextChange = Math.abs(
        (deltaUnits - roundedDeltaUnits) * unitDuration,
      );

      tickFrequency = Math.max(timeUntilNextChange, 1000);
    }

    const timeout = setTimeout(() => {
      setNow(Date.now());
    }, tickFrequency);

    return () => {
      clearTimeout(timeout);
    };
  }, [delta, value, unit, isJustNow, now]);

  if (isJustNow) {
    return "just now";
  }

  const unitDuration = UNIT_DURATION[unit];
  const deltaUnits = (value - now) / unitDuration;
  const roundedDeltaUnits =
    deltaUnits > 0 ? Math.ceil(deltaUnits) : Math.floor(deltaUnits);

  return formatter.format(roundedDeltaUnits, unit);
}

function getUnit(value: number): keyof typeof UNIT_DURATION | null {
  if (value < UNIT_DURATION.second) {
    return null;
  }

  if (value < UNIT_DURATION.minute) {
    return "second";
  }

  if (value < UNIT_DURATION.hour) {
    return "minute";
  }

  if (value < UNIT_DURATION.day) {
    return "hour";
  }

  if (value < UNIT_DURATION.week) {
    return "day";
  }

  if (value < UNIT_DURATION.month) {
    return "week";
  }

  if (value < UNIT_DURATION.year) {
    return "month";
  }

  return "year";
}

export const FormattedRelativeTime = ({
  className,
  value,
  numeric = "auto",
  style = "short",
}: {
  className?: string;
  value: Date | string;
  numeric?: Intl.RelativeTimeFormatNumeric;
  style?: Intl.RelativeTimeFormatStyle;
}) => {
  const date = useMemo(() => toDate(value), [value]);
  const formattedRelativeTime = useRelativeTime(date, { numeric, style });

  return (
    <time className={className} dateTime={date.toISOString()}>
      {formattedRelativeTime}
    </time>
  );
};

function toDate(value: string | Date): Date {
  if (value instanceof Date) {
    return value;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    throw new Error("Invalid date");
  }

  return date;
}
